import { yupResolver } from "@hookform/resolvers/yup";
import { useContext, useEffect } from "react";
import { Resolver, useFieldArray, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { toast } from "react-hot-toast";
import { http } from "shared/util/http";
import { criarSelectOptions } from "shared/util/functions/selectOptions/SelectOptionsFunctions";
import { Cond21CloudResponse } from "types/response/Cond21CloudResponse";
import showAlert from "shared/util/functions/alert/showAlert";
import {
  formatarValorMonetarioInputNumerico,
  getMesCompetenciaParaVisualizacao,
  criarDataByLocalDate,
  limparCpfCnpj,
} from "shared/util/functions/formatacoesDeValores/FormatarValores";
import { queryClient } from "shared/util/queryClient";
import { CadastrarDespesaUnicaGroupPaySchema } from "shared/yup/schemas/CadastrarDespesaUnicaGroupPaySchema";
import { UserContext } from "context/UserContext";
import { usePostValidarDespesaDuplicadaNumeroDocumentoFornecedor } from "hooks/contasPagar/usePostValidarDespesaDuplicadaGroupPay";
import { usePostCadastrarDespesaUnicaGroupPay } from "hooks/groupPay/usePostCadastrarDespesaUnicaGroupPay";
import { ClassePlanoContasYupSchemaDTO } from "types/dto/contasPagar/ClassePlanoContasYupSchemaDTO";
import { RetencoesYupSchemaDTO } from "types/dto/imposto/RetencoesYupSchemaDTO";
import { AlertEdicaoReifnDTO } from "types/dto/reinf/AlertEdicaoReifnDTO";
import { ValidarDespesaDuplicadaNumeroDocumentoFornecedorRequest } from "types/request/contasPagar/ValidarDespesaDuplicadaNumeroDocumentoFornecedorRequest";
import { CadastroContasPagarUnicaGroupPayRequest } from "types/request/groupPay/CadastroContasPagarUnicaGroupPayRequest";
import { DespesaGroupPayDetalhada } from "types/response/groupPay/DespesaGroupPayDetalhadaResponse";
import { CadastroClasseContaContasPagar } from "types/request/contasPagar/CadastroClasseContaContasPagar";
import { CadastroRetencoesContasPagarGroupPayRequest } from "types/request/contasPagar/imposto/CadastroRetencoesContasPagarGroupPayRequest";
import { useGetCondominioPossuiContaProvisionamentoAtiva } from "hooks/condominio/useGetCondominioPossuiContaProvisionamentoAtiva";
import { CadastrarDespesaUnicaGroupPayForm } from "types/reactHookForm/formTypes/CadastrarDespesaUnicaGroupPayForm";
import { AnexoItem } from "shared/components/input/InputMultiAnexos";
import { AnexoType } from "types/anexo/AnexoType";
import { useGetContaPrincipalByCondominio } from "hooks/conta/useGetContaPrincipalByCondominio";
import { TipoSerieReinf } from "constants/EnumTipoSerieReinf";
import { ContasPagarDTO } from "types/dto/contasPagar/ContasPagarDTO";
import { VerificarImpactoReinfRequest } from "types/request/reinf/VerificarImpactoReinfRequest";
import { usePostVerificarImpactoNaReinf } from "hooks/reinf/usePostVerificarImpactoNaReinf";

interface UseCadastrarDespesaUnicaProps {
  despesaGroupPay: DespesaGroupPayDetalhada | null;
  onClose: () => void;
  showModal: boolean;
}

interface RegistroFornecedorSelectAsyncItem {
  nome: string;
  value: {
    fornecedorId: number;
    nomeFornecedor: string;
    detalhesClassePlanoContas?: {
      idClassePlano: number;
      classePlanoContasNome: string;
      idContaProvisionamentoVinculada?: number;
      nomeContaProvisionamentoVinculada?: string;
    };
    naturezaRendimento?: { id: number; codigoDescricao: string };
    descricaoClassePlanoFornecedor?: string;
  };
}

const DATA_IMAGE_PNG_BASE64 = "data:image/png;base64,";

export const useCadastrarDespesaUnica = ({ despesaGroupPay, onClose, showModal }: UseCadastrarDespesaUnicaProps) => {
  const { mutateAsync: cadastrarDespesaUnica } = usePostCadastrarDespesaUnicaGroupPay();
  const { mutateAsync: validarDuplicidadeDespesa } = usePostValidarDespesaDuplicadaNumeroDocumentoFornecedor();
  const { mutateAsync: validarImpactoReinf } = usePostVerificarImpactoNaReinf();

  const { competenciaSelecionada } = useContext(UserContext);

  const { data: responseCondominioPossuiContaProvisionamento } = useGetCondominioPossuiContaProvisionamentoAtiva(
    despesaGroupPay?.idCondominio || null
  );
  const { data: responseContaPrincipal } = useGetContaPrincipalByCondominio(despesaGroupPay?.idCondominio ?? null);

  const { data: responseFornecedoresSelectAsync } = useQuery(
    ["fornecedor-select-async", despesaGroupPay?.idCondominio],
    () =>
      http.get<Cond21CloudResponse<RegistroFornecedorSelectAsyncItem[]>>(
        `/api/fornecedor/buscar-registros-select-async?condominioId=${despesaGroupPay?.idCondominio}`
      ).then((res) => res.data),
    { enabled: showModal && !!despesaGroupPay?.idCondominio }
  );

  const yupResolverInstance = yupResolver<CadastrarDespesaUnicaGroupPayForm>(CadastrarDespesaUnicaGroupPaySchema);
  const customResolver: Resolver<CadastrarDespesaUnicaGroupPayForm> = async (values, context, options) => {
    const result = await yupResolverInstance(values, context, options);
    const errors = result.errors as Record<string, unknown> | undefined;
    const retencoesError = errors?.retencoes as { message?: string; type?: string; [key: string]: unknown } | undefined;
    const hasRetencoesArrayError = retencoesError && typeof retencoesError.message === "string";

    if (hasRetencoesArrayError && values.retencoes?.length) {
      const newRetencoesError = { ...retencoesError };
      values.retencoes.forEach((_: RetencoesYupSchemaDTO, index: number) => {
        (newRetencoesError as Record<string, unknown>)[String(index)] = {
          ...((newRetencoesError as Record<string, unknown>)[String(index)] as object),
          valor: { type: "custom" as const, message: "" },
        };
      });
      result.errors = { ...result.errors, retencoes: newRetencoesError };
    }
    return result;
  };

  const form = useForm<CadastrarDespesaUnicaGroupPayForm>({
    resolver: customResolver,
    defaultValues: {
      classesConta: [],
      retencoes: [],
      tipoFormaPagamento: null,
      anexos: [],
      valorLiquido: 0,
      valorPago: 0,
      juros: 0,
      multa: 0,
      desconto: 0,
      incluirAcrescimosDescontos: false,
    },
  });

  const { control, setValue, watch, reset, formState } = form;

  const {
    fields: classesConta,
    append: adicionarClasseConta,
    remove: removerClasseConta,
  } = useFieldArray({ control: control, name: "classesConta" });

  const {
    fields: retencoes,
    append: adicionarRetencao,
    remove: removerRetencao,
  } = useFieldArray({ control: control, name: "retencoes" });

  const classesContaForm = watch("classesConta", []);
  const retencoesForm = watch("retencoes", []);

  const juros = watch("juros") || 0;
  const multa = watch("multa") || 0;
  const desconto = watch("desconto") || 0;

  const valorBrutoTotal = classesContaForm.reduce((total, classe) => {
    const valor = classe.valorBruto || 0;
    const valorNumerico = typeof valor === "number" ? valor : formatarValorMonetarioInputNumerico(String(valor));
    return total + (isNaN(valorNumerico) ? 0 : valorNumerico);
  }, 0);

  const valorTotalImpostos = retencoesForm.reduce((total, retencao) => {
    const valor = retencao.valor || 0;
    const valorNumerico = typeof valor === "number" ? valor : formatarValorMonetarioInputNumerico(String(valor));
    return total + (isNaN(valorNumerico) ? 0 : valorNumerico);
  }, 0);

  useEffect(() => {
    const valorLiquidoCalculado = valorBrutoTotal - valorTotalImpostos;
    const valorLiquido = watch("valorLiquido") || 0;

    if (valorLiquido !== valorLiquidoCalculado) {
      setValue("valorLiquido", valorLiquidoCalculado);
    }

    const jurosNumerico = formatarValorMonetarioInputNumerico(String(juros));
    const multaNumerico = formatarValorMonetarioInputNumerico(String(multa));
    const descontoNumerico = formatarValorMonetarioInputNumerico(String(desconto));

    const valorPagoCalculado = valorLiquidoCalculado + jurosNumerico + multaNumerico - descontoNumerico;
    const valorFinal = valorPagoCalculado > 0 ? valorPagoCalculado : 0;

    if (watch("valorPago") !== valorFinal) {
    setValue("valorPago", valorFinal);
    }
  }, [valorBrutoTotal, valorTotalImpostos, juros, multa, desconto, setValue, watch]);

  useEffect(() => {
    if (showModal && despesaGroupPay) {
      let retencoesDefault: RetencoesYupSchemaDTO[] = [];
      if (
        despesaGroupPay.sugestoesDeImpostosParaCadastro &&
        despesaGroupPay.sugestoesDeImpostosParaCadastro.length > 0
      ) {
        retencoesDefault = despesaGroupPay.sugestoesDeImpostosParaCadastro.map((sugestao) => ({
          imposto: {
            label: sugestao.descricaoImposto,
            value: { impostoId: sugestao.impostoId, aliquota: sugestao.aliquota, tipoImposto: sugestao.tipoImposto },
          },
          valor: sugestao.valorImposto,
          valorBase: despesaGroupPay.valor,
          dataVencimento: despesaGroupPay.dataVencimento,
          mesCompetencia: competenciaSelecionada,
          contribuinte: null,
          conta: null,
          formaPagamento: null,
          codigoDeBarras: "",
          anexo: null,
        }));
      } else {
        retencoesDefault = [
          {
            imposto: null,
            valorBase: despesaGroupPay.valor,
            valor: undefined,
            mesCompetencia: null,
            dataVencimento: null,
            contribuinte: null,
            conta: null,
            formaPagamento: null,
            codigoDeBarras: "",
            anexo: null,
          },
        ];
      }

      const valorBrutoInicial = Number(despesaGroupPay.valor) || 0;
      const valorTotalImpostosInicial = retencoesDefault.reduce<number>((acc, r) => acc + (Number(r.valor) || 0), 0);
      const valorLiquidoInicial = Math.max(valorBrutoInicial - valorTotalImpostosInicial, 0);

      reset({
        condominio: { label: despesaGroupPay.nomeCondominio || "", value: despesaGroupPay.idCondominio },
        fornecedor: despesaGroupPay.idFornecedorEmissor
          ? {
              label: despesaGroupPay.nomeEmissor || "",
              value: { fornecedorId: despesaGroupPay.idFornecedorEmissor },
            }
          : {
              label: despesaGroupPay.nomeEmissor || "",
              value: { nomeFornecedor: despesaGroupPay.nomeEmissor || "", fornecedorId: null },
            },
        conta: {
          label: responseContaPrincipal?.payload?.descricao ?? "",
          value: responseContaPrincipal?.payload,
        },
        mesCompetencia: competenciaSelecionada,
        classesConta: [
          {
            classePlano: {
              label: despesaGroupPay.classePlanoContaVinculoFornecedorResponse?.classePlanoContaNome ?? "",
              value: despesaGroupPay.classePlanoContaVinculoFornecedorResponse
                ? {
                    idClassePlano: despesaGroupPay.classePlanoContaVinculoFornecedorResponse.classePlanoContaId,
                    idContaProvisionamentoVinculada:
                      despesaGroupPay.classePlanoContaVinculoFornecedorResponse.idContaProvisionamentoVinculada,
                    nomeContaProvisionamentoVinculada:
                      despesaGroupPay.classePlanoContaVinculoFornecedorResponse.nomeContaProvisionamentoVinculada,
                  }
                : {},
            },
            valorBruto: despesaGroupPay.valor,
            descricao: despesaGroupPay.classePlanoContaVinculoFornecedorResponse?.descricao ?? "",
            condominioPossuiContaProvisionamentoAtiva: responseCondominioPossuiContaProvisionamento?.payload,
            contaProvisionamento: despesaGroupPay.classePlanoContaVinculoFornecedorResponse
              ?.idContaProvisionamentoVinculada
              ? {
                  label:
                    despesaGroupPay.classePlanoContaVinculoFornecedorResponse.nomeContaProvisionamentoVinculada || "",
                  value:
                    despesaGroupPay.classePlanoContaVinculoFornecedorResponse.idContaProvisionamentoVinculada || "",
                }
              : null,
          },
        ],
        naturezaRendimento: {
          label: despesaGroupPay?.naturezaRendimentoDTO?.codigoDescricao,
          value: despesaGroupPay?.naturezaRendimentoDTO?.id,
        },
        retencoes: retencoesDefault,
        anexos: [],
        numeroDocumento: despesaGroupPay.numeroDocumento ?? "",
        serieNf: despesaGroupPay.serieNf ?? "",
        dataEmissao: criarDataByLocalDate(String(despesaGroupPay.dataEmissao)) ?? null,
        dataVencimento: criarDataByLocalDate(String(despesaGroupPay.dataVencimento)) ?? null,
        dataPagamento: null,
        observacao: despesaGroupPay.observacao ?? "",
        codigoCopiaColaPix: despesaGroupPay.codigoCopiaColaPix ?? "",
        codigoDeBarras: despesaGroupPay.codigoDeBarras ?? "",
        valorLiquido: valorLiquidoInicial,
        valorPago: valorLiquidoInicial,
        juros: 0,
        multa: 0,
        desconto: 0,
        incluirAcrescimosDescontos: false,
      });
    }
  }, [form, despesaGroupPay, showModal, competenciaSelecionada, responseCondominioPossuiContaProvisionamento]);

  useEffect(() => {
    if (
      !showModal ||
      !despesaGroupPay?.idCondominio ||
      !responseFornecedoresSelectAsync?.payload?.length ||
      (!despesaGroupPay.idFornecedorEmissor && !despesaGroupPay.nomeEmissor?.trim())
    ) {
      return;
    }
    const list = responseFornecedoresSelectAsync.payload;
    const nomeEmissorNorm = despesaGroupPay.nomeEmissor?.trim().toUpperCase() ?? "";
    const item = list.find((reg) => {
      if (despesaGroupPay.idFornecedorEmissor != null) {
        return reg.value.fornecedorId === despesaGroupPay.idFornecedorEmissor;
      }
      return (
        reg.value.nomeFornecedor?.trim().toUpperCase() === nomeEmissorNorm ||
        reg.nome?.trim().toUpperCase() === nomeEmissorNorm
      );
    });
    if (!item) return;

    setValue("fornecedor", { label: item.nome, value: item.value });

    const v = item.value;
    if (v.detalhesClassePlanoContas?.idClassePlano) {
      setValue(
        "classesConta.0.classePlano",
        criarSelectOptions(
          { idClassePlano: v.detalhesClassePlanoContas.idClassePlano },
          v.detalhesClassePlanoContas.classePlanoContasNome ?? ""
        )
      );
      setValue("classesConta.0.descricao", v.descricaoClassePlanoFornecedor ?? "");
      if (v.detalhesClassePlanoContas.idContaProvisionamentoVinculada) {
        setValue(
          "classesConta.0.contaProvisionamento",
          criarSelectOptions(
            v.detalhesClassePlanoContas.idContaProvisionamentoVinculada,
            v.detalhesClassePlanoContas.nomeContaProvisionamentoVinculada ?? ""
          )
        );
      }
    } else {
      setValue("classesConta.0.classePlano", null);
      setValue("classesConta.0.descricao", null);
      setValue("classesConta.0.contaProvisionamento", null);
    }
    if (v.naturezaRendimento) {
      setValue(
        "naturezaRendimento",
        criarSelectOptions(v.naturezaRendimento.id, v.naturezaRendimento.codigoDescricao)
      );
    } else {
      setValue("naturezaRendimento", null);
    }
  }, [
    showModal,
    despesaGroupPay,
    responseFornecedoresSelectAsync?.payload,
    setValue,
  ]);

  const mapearAnexosParaRequest = (anexos: AnexoItem[] | null, isRetencao: boolean = false): AnexoType[] => {
    if (!anexos) return [];

    return anexos?.map((anexo) => ({
      nome: anexo.nome,
      base64: DATA_IMAGE_PNG_BASE64 + anexo.arquivo || "",
      anexoComprovante: isRetencao ? true : false,
    }));
  };

  const montarCadastroContasPagarUnicaGroupPayRequest = (
    form: CadastrarDespesaUnicaGroupPayForm,
    despesa: DespesaGroupPayDetalhada,
    possuiContaProvisionamento: boolean
  ): CadastroContasPagarUnicaGroupPayRequest => {
    const deveCriarFornecedor = !form?.fornecedor?.value?.fornecedorId && !!despesaGroupPay?.nomeEmissor;
    const request: CadastroContasPagarUnicaGroupPayRequest = {
      idDespesaGroupPay: despesa.id,
      condominioId: form.condominio.value,
      fornecedorId: form.fornecedor.value.fornecedorId,
      nomeFornecedor: deveCriarFornecedor ? despesaGroupPay?.nomeEmissor ?? null : null,
      cpfCnpjFornecedor:
        deveCriarFornecedor && despesaGroupPay?.cpfCnpjEmissor
          ? limparCpfCnpj(despesaGroupPay.cpfCnpjEmissor)
          : null,
      favorecidoFornecedorId: form.isFavorecidoFornecedor ? form.favorecido?.value.fornecedorId : undefined,
      naturezaRendimentoId: form?.naturezaRendimento?.value,

      mesCompetencia: form.mesCompetencia.toISOString(),
      dataVencimento: form.dataVencimento.toISOString(),
      dataEmissao: form.dataEmissao?.toISOString() ?? null,

      numeroDocumento: form.numeroDocumento,
      serieNf: form.serieNf,
      observacao: form.observacao,

      classesConta: form.classesConta.map((classeConta) =>
        montarCadastroClasseContaContasPagar(classeConta, possuiContaProvisionamento)
      ),
      retencoes: form.retencoes
        .filter((retencao) => retencao.imposto)
        .map((retencao) => montarCadastroRetencaoContasPagar(retencao)),

      dataPagamento: form.dataPagamento?.toISOString() ?? null,
      dataLiquidacao: form.dataLiquidacao?.toISOString() ?? null,
      contaId: form.conta?.value ? Number(form.conta.value.id) : null,
      tipoFormaPagamento: form.tipoFormaPagamento?.value,
      tipoFormaPagamentoPix: form.tipoFormaPagamentoPix,
      codigoDeBarras: form.codigoDeBarras || null,
      codigoCopiaColaPix: form.codigoCopiaColaPix,
      valorPago: form.valorPago,

      juros: form.juros ? formatarValorMonetarioInputNumerico(String(form.juros)) : null,
      multa: form.multa ? formatarValorMonetarioInputNumerico(String(form.multa)) : null,
      desconto: form.desconto ? formatarValorMonetarioInputNumerico(String(form.desconto)) : null,
      anexos: form.anexos && form.anexos.length > 0 ? mapearAnexosParaRequest(form.anexos, false) : null,

      cheque: form.numeroCheque ? { numero: form.numeroCheque } : null,
      incluirAcrescimosDescontos: form.incluirAcrescimosDescontos || false,
    };
    return request;
  };

  const montarCadastroClasseContaContasPagar = (
    classeConta: ClassePlanoContasYupSchemaDTO,
    possuiContaProvisionamento: boolean
  ): CadastroClasseContaContasPagar => {
    const cadastroClasseConta: Partial<CadastroClasseContaContasPagar> = {
      classeContaId: classeConta?.classePlano?.value.idClassePlano,
      valor: classeConta.valorBruto,
      descricao: classeConta.descricao,
    };
    if (possuiContaProvisionamento) {
      cadastroClasseConta.contaProvisionamentoId = classeConta.contaProvisionamento?.value;
    }
    return cadastroClasseConta as CadastroClasseContaContasPagar;
  };

  const montarCadastroRetencaoContasPagar = (
    retencao: RetencoesYupSchemaDTO
  ): CadastroRetencoesContasPagarGroupPayRequest => {
    return {
      impostoId: retencao?.imposto?.value.impostoId ?? null,
      valorBase: retencao.valorBase ?? null,
      valor: retencao.valor ?? null,
      mesCompetencia: retencao.mesCompetencia?.toISOString() ?? null,
      dataVencimento: retencao.dataVencimento?.toISOString() ?? null,
      contribuinte: retencao.contribuinte?.value ?? null,
      contaId: retencao.conta?.value ? Number(retencao.conta.value.id) : null,
      tipoFormaPagamento: retencao.formaPagamento?.value ?? null,
      tipoFormaPagamentoPix: retencao.tipoFormaPagamentoPix ?? null,
      codigoCopiaColaPix: retencao.codigoCopiaColaPix ?? null,
      codigoDeBarras: retencao.codigoDeBarras ?? null,
      anexo: retencao.anexo ? mapearAnexosParaRequest(retencao?.anexo, true) : null,
    };
  };

  const getTextoAlertaReinf = (alertaReinf: AlertEdicaoReifnDTO, mesCompetencia: Date) => {
    let mesCompetenciaFormatado = getMesCompetenciaParaVisualizacao(mesCompetencia);
    let mensagemDeAlerta = "";
    if (alertaReinf.existeEmProcessamentoMesCompetencia || alertaReinf.existeEmProcessamentoMesCompetenciaOriginal) {
      mensagemDeAlerta = "Não é possivel cadastrar a despesa. Aguardando retorno de processamento da EFD-Reinf.";
    } else if (alertaReinf.existeEnviadaMesCompetencia || alertaReinf.existeEnviadaMesCompetenciaOriginal) {
      mensagemDeAlerta = `A EFD-Reinf já foi enviada na competência ${mesCompetenciaFormatado}. Ao cadastrar uma nova despesa, será necessário realizar um novo envio. Lembrando que qualquer informação incorreta ou envio fora do prazo, o condomínio estará sujeito a multa.`;
    }
    return mensagemDeAlerta;
  };

  const deveEmitirAvisoReinf = (validacoesDaReinf: AlertEdicaoReifnDTO) => {
    return validacoesDaReinf.existeEnviadaMesCompetencia || validacoesDaReinf.existeEnviadaMesCompetenciaOriginal;
  };

  const deveExibirAlertaBloqueioDeCadastroDeDespesaReinf = (validacoesDaReinf: AlertEdicaoReifnDTO) => {
    return (
      validacoesDaReinf.existeEmProcessamentoMesCompetencia ||
      validacoesDaReinf.existeEmProcessamentoMesCompetenciaOriginal
    );
  };

  const montarImpostosParaValidacaoReinf = (retencoesForm: RetencoesYupSchemaDTO[] = []) => {
    return retencoesForm
      .filter((r) => !!r?.imposto?.value?.impostoId)
      .map((retencao) => ({
        mesCompetencia: retencao.mesCompetencia?.toISOString(),
        impostoId: retencao.imposto!.value.impostoId,
      })) as ContasPagarDTO[];
  };

  const getImpactosDaReinf = async (form: CadastrarDespesaUnicaGroupPayForm) => {
    const mesCompetencia = new Date(form.mesCompetencia as Date);
    mesCompetencia.setDate(1);

    const contasPagarDTO: ContasPagarDTO = {
      mesCompetencia: mesCompetencia.toISOString(),
      impostos: montarImpostosParaValidacaoReinf(form.retencoes || []),
    };

    const requestReinf2000: VerificarImpactoReinfRequest = {
      contasPagarDTO: contasPagarDTO,
      tipoSerieReinf: TipoSerieReinf.SERIE_2000,
    };

    const requestReinf4000: VerificarImpactoReinfRequest = {
      contasPagarDTO: contasPagarDTO,
      tipoSerieReinf: TipoSerieReinf.SERIE_4000,
    };

    try {
      const [alertaReinf2000, alertaReinf4000] = await Promise.all([
        validarImpactoReinf(requestReinf2000),
        validarImpactoReinf(requestReinf4000),
      ]);

      return { alertaReinf2000, alertaReinf4000 };
    } catch (error) {
      toast.error("Erro ao verificar impacto na REINF");
      return {
        alertaReinf2000: {} as AlertEdicaoReifnDTO,
        alertaReinf4000: {} as AlertEdicaoReifnDTO,
      };
    }
  };

  const isErroDeDataInferior = (error: any): boolean => {
    return error?.response?.data?.validation[0]?.groupCode === "contasPagar.error.dataInferior";
  };

  const cadastrarDespesa = async (request: CadastroContasPagarUnicaGroupPayRequest) => {
    try {
      await cadastrarDespesaUnica(request);
      onClose();
      queryClient.invalidateQueries(["despesas-paginadas-group-pay"]);
    } catch (err) {
      if (isErroDeDataInferior(err)) {
        return;
      }
    }
  };

  const salvarDespesaUnica = async (formData: CadastrarDespesaUnicaGroupPayForm) => {
    if (formState.errors && Object.keys(formState.errors).length > 0) return;

    if (!despesaGroupPay) {
      toast.error("Erro: dados da despesa não encontrados. Tente novamente.");
      return;
    }

    let request = montarCadastroContasPagarUnicaGroupPayRequest(
      formData,
      despesaGroupPay,
      responseCondominioPossuiContaProvisionamento?.payload ?? false
    );

    if (valorBrutoTotal < valorTotalImpostos) {
      const msg =
        "O somatório dos valores informados para retenções não pode ser superior ao valor bruto total.";
      form.setError("retencoes", { type: "somaMaiorQueBruto", message: msg });
      formData.retencoes?.forEach((_, index) => {
        form.setError(`retencoes.${index}.valor`, { type: "custom", message: msg });
      });
      toast.error(msg);
      return;
    }

    const validacao: ValidarDespesaDuplicadaNumeroDocumentoFornecedorRequest = {
      fornecedorId: request.fornecedorId,
      numeroDocumento: despesaGroupPay.numeroDocumento,
      condominioId: despesaGroupPay.idCondominio,
    };

    let validacoesDaReinfSerie2000 = {} as AlertEdicaoReifnDTO;
    let validacoesDaReinfSerie4000 = {} as AlertEdicaoReifnDTO;

    if (formData.retencoes.filter((r) => r.imposto != null).length > 0) {
      ({ alertaReinf2000: validacoesDaReinfSerie2000, alertaReinf4000: validacoesDaReinfSerie4000 } =
        await getImpactosDaReinf(formData));
    }

    if (
      deveExibirAlertaBloqueioDeCadastroDeDespesaReinf(validacoesDaReinfSerie2000) ||
      deveExibirAlertaBloqueioDeCadastroDeDespesaReinf(validacoesDaReinfSerie4000)
    ) {
      const textoAlerta = deveExibirAlertaBloqueioDeCadastroDeDespesaReinf(validacoesDaReinfSerie2000)
        ? getTextoAlertaReinf(validacoesDaReinfSerie2000, formData.mesCompetencia)
        : getTextoAlertaReinf(validacoesDaReinfSerie4000, formData.mesCompetencia);
      showAlert({
        texto: textoAlerta,
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        labelBotaoCancelar: "Cancelar",
        labelBotaoConfirmar: "OK",
      });
      return;
    }

    if (deveEmitirAvisoReinf(validacoesDaReinfSerie2000) || deveEmitirAvisoReinf(validacoesDaReinfSerie4000)) {
      const textoAlerta = deveEmitirAvisoReinf(validacoesDaReinfSerie2000)
        ? getTextoAlertaReinf(validacoesDaReinfSerie2000, formData.mesCompetencia)
        : getTextoAlertaReinf(validacoesDaReinfSerie4000, formData.mesCompetencia);
      showAlert({
        texto: textoAlerta,
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: async () => {
          try {
            await validarDuplicidadeDespesa(validacao);
            await cadastrarDespesa(request);
          } catch (error: any) {
            if (
              error.response?.data?.validation[0]?.groupCode ===
              "contasPagar.error.duplicidadeNumeroDocumentoFornecedor"
            ) {
              showAlert({
                texto: "Já existe uma despesa com o mesmo número de documento e fornecedor. Deseja continuar?",
                tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
                onConfirm: () => cadastrarDespesa(request).catch(() => {}),
              });
            }
          }
        },
      });
      return;
    }

    try {
      await validarDuplicidadeDespesa(validacao);
      await cadastrarDespesa(request);
    } catch (error: any) {
      if (
        error?.response?.data?.validation[0]?.groupCode === "contasPagar.error.duplicidadeNumeroDocumentoFornecedor"
      ) {
        showAlert({
          texto: "Já existe uma despesa com o mesmo número de documento e fornecedor. Deseja continuar?",
          tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
          onConfirm: () => cadastrarDespesa(request).catch(() => {}),
        });
      }
    }
  };

  const onSubmit = form.handleSubmit(salvarDespesaUnica);

  const handleCloseModal = () => {
    if (formState.isDirty) {
      showAlert({
        texto: "Existem alterações que não foram salvas na tela. Tem certeza que deseja sair sem salvar?",
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: onClose,
      });
    } else {
      onClose();
    }
  };

  return {
    form,
    onSubmit,
    watch: watch,
    classesConta,
    adicionarClasseConta,
    removerClasseConta,
    retencoes,
    adicionarRetencao,
    removerRetencao,
    valorBrutoTotal,
    valorTotalImpostos,
    handleCloseModal,
  };
};
